import React, { useState, useEffect, useMemo } from "react";
import { Search, Grid, List, FileAxis3D, FileAxis3DIcon, Filter } from "lucide-react";
import { SubdomainCard } from "./SubdomainCard";
import { MetricCard } from "../ui/MetricCard";
import { Button } from "../ui/Button";
import { FileText } from "lucide-react";
import ModelSummary from "./ModelSummary";
import { CardMetric } from "../ui/CardMetric";
import axios from "axios";
import Select from "react-select";

const dummyData = "The Marketing Domain encompasses all data assets related to the planning, execution, measurement, and optimization of marketing strategies and activities. It includes both internal and external data that inform customer engagement, campaign performance, market research, brand positioning, and promotional effectiveness.\r\nPurpose:\r\nTo support data-driven decision-making across marketing functions by ensuring data is accurate, consistent, accessible, and aligned with business objectives. The domain facilitates targeted outreach, customer segmentation, ROI analysis, and personalized experiences across channels.\r\nKey Data Entities:\r\n•\tCampaign Data: Details of marketing campaigns, including goals, budgets, timelines, and performance metrics.\r\n•\tCustomer Segments: Definitions and criteria for customer groups based on demographics, behavior, or engagement levels.\r\n•\tLeads and Prospects: Information on potential customers gathered through various channels and touchpoints.\r\n•\tDigital Engagement Metrics: Click-through rates, impressions, bounce rates, and other KPIs from digital platforms.\r\n•\tBrand and Market Research: Insights from surveys, focus groups, sentiment analysis, and competitive benchmarking.\r\n•\tPromotions and Offers: Data on discounts, promotions, and their redemption history and effectiveness.\r\nData Sources:\r\n•\tCRM systems\r\n•\tMarketing automation platforms (e.g., HubSpot, Marketo)\r\n•\tWeb and social media analytics tools (e.g., Google Analytics, Facebook Insights)\r\n•\tCustomer feedback tools\r\n•\tExternal market research and demographic data providers\r\nKey Stakeholders:\r\n•\tMarketing and Brand Managers\r\n•\tDigital Marketing Analysts\r\n•\tCustomer Experience Teams\r\n•\tData Analysts and Data Scientists\r\n•\tSales and Business Development Teams\r\nGovernance Considerations:\r\n•\tData Quality: Ensure campaign and customer data is complete, timely, and accurate.\r\n•\tPrivacy and Compliance: Adhere to GDPR, CCPA, and other data protection regulations, particularly when handling customer PII.\r\n•\tIntegration: Ensure seamless data flows between marketing platforms, CRM systems, and data warehouses.\r\n•\tMetadata Management: Maintain clear definitions and lineage for all marketing-related data assets."

// Data Layer options from productFormConfig
const DATA_LAYER_OPTIONS = [
  { value: "ingestion", label: "Ingestion" },
  { value: "bronze", label: "Bronze" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "analyticalProduct", label: "Analytical Product" }
];

export const DomainOverview = ({ domain, onSubdomainClick, onFilterChange, appliedFilters }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  
  // Initialize filter states from props to prevent flash
  // Use useMemo to compute initial values only once
  const initialSelectedRegions = useMemo(() => {
    if (appliedFilters?.regions && appliedFilters.regions.length > 0) {
      return appliedFilters.regions.map(region => ({
        value: region,
        label: region
      }));
    }
    return [];
  }, []); // Empty deps - only compute on mount

  const initialDataLayer = useMemo(() => {
    if (appliedFilters?.dataLayer && appliedFilters.dataLayer.length > 0) {
      // Handle dataLayer as array
      return appliedFilters.dataLayer.map(layerValue => 
        DATA_LAYER_OPTIONS.find(option => option.value === layerValue)
      ).filter(Boolean);
    }
    return [];
  }, []); // Empty deps - only compute on mount

  const initialFiltersApplied = useMemo(() => {
    return (appliedFilters?.regions?.length > 0 || appliedFilters?.dataLayer?.length > 0) ? true : false;
  }, []); // Empty deps - only compute on mount
  
  // Filter states
  const [showFilters, setShowFilters] = useState(initialFiltersApplied);
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState(initialSelectedRegions);
  const [selectedDataLayer, setSelectedDataLayer] = useState(initialDataLayer);
  const [filtersApplied, setFiltersApplied] = useState(initialFiltersApplied);

  // Filter subdomains based on search term
  const filteredSubdomains = useMemo(() => {
    if (!searchTerm.trim()) {
      return domain.domains_L2;
    }
    
    return domain.domains_L2.filter(
      (subdomain) =>
        subdomain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subdomain.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [domain.domains_L2, searchTerm]);

  // Calculate pagination values
  const totalPages = Math.max(1, Math.ceil(filteredSubdomains.length / itemsPerPage));
  
  // Ensure current page is valid when filtered results change
  const validCurrentPage = Math.min(currentPage, totalPages);
  
  const indexOfFirstItem = (validCurrentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, filteredSubdomains.length);
  const currentSubdomains = filteredSubdomains.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Update current page if it becomes invalid due to filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Fetch regions on component mount
  useEffect(() => {
    // TODO: Uncomment when API is ready
    const fetchRegions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/parent-region`);
        setRegions(response?.data?.parent_regions || []);
      } catch (error) {
        console.error("Error fetching regions:", error);
        setRegions([]);
      }
    };
    fetchRegions().catch(console.error);

    // Static regions data (temporary until API is ready)
    // const staticRegions = [
    //   "Western Europe",
    //   "Eastern Europe",
    //   "North America",
    //   "South America",
    //   "Asia Pacific",
    //   "Middle East",
    //   "Africa",
    //   "Nordics",
    //   "UK & Ireland",
    //   "Germany",
    //   "France",
    //   "Italy",
    //   "Spain",
    //   "Benelux",
    //   "Switzerland",
    //   "Austria"
    // ];
    // setRegions(staticRegions);
  }, []);

  // Keep filters panel open when filters are applied
  useEffect(() => {
    if (filtersApplied) {
      setShowFilters(true);
    }
  }, [filtersApplied]);

  // Metrics calculations
  const totalAssets = domain.domains_L2.reduce(
    (sum, sub) => sum + parseInt(sub.assets || 0),
    0
  );
  const totalProducts = domain.domains_L2.reduce(
    (sum, sub) => sum + parseInt(sub.product_count || 0),
    0
  );
  // const averageScore = domain.domains_L2.length > 0
  //   ? domain.domains_L2.reduce(
  //       (sum, sub) => sum + parseFloat(sub.score.replace("%", "") || 0),
  //       0
  //     ) / domain.domains_L2.length
  //   : 0;
    const averageScore = domain?.score ? domain?.score : "0%";


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Convert regions to react-select options
  const regionOptions = useMemo(() => {
    return regions.map(region => ({ value: region, label: region }));
  }, [regions]);

  // Handle region selection (multiselect)
  const handleRegionChange = (selectedOptions) => {
    setSelectedRegions(selectedOptions || []);
  };

  // Handle data layer selection (multiselect)
  const handleDataLayerChange = (selectedOptions) => {
    setSelectedDataLayer(selectedOptions || []);
  };

  // Apply filters
  const handleApplyFilters = () => {
    if (onFilterChange) {
      const regionValues = selectedRegions.map(r => r.value);
      const dataLayerValues = Array.isArray(selectedDataLayer) 
        ? selectedDataLayer.map(d => d.value) 
        : [];
      onFilterChange(regionValues, dataLayerValues);
      setFiltersApplied(true);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, validCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === 1}
        onClick={() => handlePageChange(validCurrentPage - 1)}
      >
        Previous
      </button>
    );

    // First page and ellipsis
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`px-3 py-2 text-sm border rounded-md transition-colors ${
            validCurrentPage === i
              ? "bg-blue-500 text-white border-blue-500 shadow-sm"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page and ellipsis
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === totalPages}
        onClick={() => handlePageChange(validCurrentPage + 1)}
      >
        Next
      </button>
    );

    return buttons;
  };

  return (
    <div className="space-y-7">
      {/* Domain Header */}
      <div className="rounded-lg py-2 text-white relative p-[20px]">
      <div style={{alignContent:"center"}} className="flex space-x-1 mb-2 align-center gap-2 mb-3">

        {/* <FileAxis3DIcon className="text-[#bb1ccc]" /> */}
        <h1 className="text-3xl font-bold text-[#424242]">
          {domain.parent_L1} Domain
        </h1>


        <div className="mb-4 absolute top-0 right-10">
        <ModelSummary
          title={domain.parent_L1}
          text={domain.parent_L1_description_expanded || "N/A"}
        />
      </div>
    </div>

        <p className="text-white-500 text-lg leading-relaxed text-left text-black">
          {domain.parent_L1_description}
        </p>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-[25px] " >
       
        <CardMetric
          title="Total Products" 
          value={totalProducts.toLocaleString()} 
          variant="success" 
          subtitle="Active products"
          performance={92}
        />
        <CardMetric
          title="Quality Score"
          value={averageScore}
          variant="warning"
          subtitle="Overall quality"
          performance={92}
        />
         <CardMetric
          title="Total Assets" 
          value={totalAssets.toLocaleString()} 
          variant="primary" 
          subtitle="Across all domains"
          performance={92}
        />
      </div>

      {/* Controls and Filters */}
      <div className="space-y-4 pb-[20px] pl-[20px] pr-[20px]">
        {/* First Row: Search, Show Filters Button and View Mode */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search style={{width:"15px"}} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search for domains..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{paddingLeft:"35px"}}
                className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[0000a0] focus:border-transparent transition-colors h-[45px]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] whitespace-nowrap ${
                showFilters || filtersApplied
                  ? 'bg-[#bb1ccc] text-white border-[#bb1ccc]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {/* <Filter size={18} /> */}
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
              {/* {filtersApplied && (
                <span className="bg-white text-[#bb1ccc] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {(selectedRegions.length + (selectedDataLayer ? 1 : 0))}
                </span>
              )} */}
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] ${
                viewMode === "grid"
                  ? 'bg-[#bb1ccc] text-white border-[#bb1ccc]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Grid size={18} />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] ${
                viewMode === "table"
                  ? 'bg-[#bb1ccc] text-white border-[#bb1ccc]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <List size={18} />
              <span>Table</span>
            </button>
          </div>
        </div>

        {/* Second Row: Filters (Collapsible) */}
        {showFilters && (
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
          {/* Region Multiselect */}
          <div className="flex-1 min-w-[300px] max-w-[400px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Region
            </label>
            <Select
              isMulti
              value={selectedRegions}
              onChange={handleRegionChange}
              options={regionOptions}
              placeholder="Select regions..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '45px',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e0e7ff',
                  borderRadius: '4px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#3730a3',
                  fontWeight: '500'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#3730a3',
                  '&:hover': {
                    backgroundColor: '#c7d2fe',
                    color: '#312e81'
                  }
                })
              }}
            />
          </div>

          {/* Data Layer Multiselect */}
          <div className="flex-1 min-w-[250px] max-w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Layer
            </label>
            <Select
              isMulti
              value={selectedDataLayer}
              onChange={handleDataLayerChange}
              options={DATA_LAYER_OPTIONS}
              placeholder="Select data layers..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '45px',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e0e7ff',
                  borderRadius: '4px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#3730a3',
                  fontWeight: '500'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#3730a3',
                  '&:hover': {
                    backgroundColor: '#c7d2fe',
                    color: '#312e81'
                  }
                })
              }}
            />
          </div>

          {/* Apply Filters Button */}
          <div className="flex items-end" style={{ paddingTop: '24px' }}>
            <button
              onClick={handleApplyFilters}
              disabled={selectedRegions.length === 0 && selectedDataLayer.length === 0}
              className="px-6 py-2.5 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-[#bb1ccc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[45px] whitespace-nowrap"
            >
              Apply Filters
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between pl-[20px] pr-[20px] pt-[5px] pb-[5px]">
        <h2 className="text-2xl font-bold text-gray-900">
          {searchTerm ? 'Search Results' : 'Domains'}
        </h2>
        <div className="text-sm text-gray-600">
          {searchTerm && (
            <span className="mr-4">
              {filteredSubdomains.length} result{filteredSubdomains.length !== 1 ? 's' : ''} for "{searchTerm}"
            </span>
          )}
          <span>
            {filteredSubdomains.length} of {domain.domains_L2.length} domains
          </span>
        </div>
      </div>

      {/* Content */}
      {filteredSubdomains.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {searchTerm ? 'No domains found' : 'No domains available'}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {searchTerm 
              ? `No domains match "${searchTerm}". Try adjusting your search terms.`
              : 'There are no domains to display at this time.'
            }
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-[4px] pb-[4px] pl-2 pr-2">
              {currentSubdomains.map((subdomain) => (
                <SubdomainCard
                  key={`${subdomain.name}-${validCurrentPage}`}
                  subdomain={subdomain}
                  onClick={() => onSubdomainClick(subdomain.name)}
                />
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm mr-[20px] ml-[20px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quality Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assets
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Products
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentSubdomains.map((subdomain) => (
                    <tr
                      key={`${subdomain.name}-${validCurrentPage}-table`}
                      onClick={() => onSubdomainClick(subdomain.name)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {subdomain.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {subdomain.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            parseFloat(subdomain.score.replace("%", "")) >= 90
                              ? "bg-green-100 text-green-800"
                              : parseFloat(subdomain.score.replace("%", "")) >= 80
                              ? "bg-blue-100 text-blue-800"
                              : "bg-orange-100 text-orange-800"
                          }`}
                        >
                          {subdomain.score}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {parseInt(subdomain.assets || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {parseInt(subdomain.product_count || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                <span className="font-medium">{indexOfLastItem}</span> of{" "}
                <span className="font-medium">{filteredSubdomains.length}</span> results
                {searchTerm && (
                  <span className="text-gray-500"> (filtered from {domain.domains_L2.length} total)</span>
                )}
              </div>
              <div className="flex space-x-1">
                {renderPaginationButtons()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};